import _compact from "lodash/compact";
import _find from "lodash/find";
import _flatten from "lodash/flatten";
import _forEach from "lodash/forEach";
import _get from "lodash/get";
import _includes from "lodash/includes";
import _isEmpty from "lodash/isEmpty";
import _keys from "lodash/keys";
import _size from "lodash/size";
import _sortBy from "lodash/sortBy";
import _split from "lodash/split";
import _values from "lodash/values";
import moment from "moment";
import {
    getAccountId,
    getAccountsWithSubscriptions,
    getExistingSubscriptionsInGroup,
    hasExternalLegalOwner,
} from "./accountSelectors";
import {
    getAllItemsOfType,
    getCartContent,
    getCustomerTypeGet,
    getFirstBundleItemInCart,
    getFlattenedCartContent,
    getItemByBundleId,
    getTotalPriceUpfront,
    hasCartHardware,
} from "./cartSelectors";
import { State as CheckoutState } from "./checkout";
import * as CUSTOMER_INFO_SELECTORS from "./customerInfoSelectors";
import {
    getDeliveryDescription,
    getDeliveryInfoAddress,
    getDeliveryInfoType,
    getDeliveryInfoSelectedPrice,
    getEnhancedDeliveryInfoSelected,
} from "./deliveryInfoSelectors";
import TEXT from "../../text-consts";
import { getFormById } from "./formSelectors";
import { getPhoneNumberCheckType, getSelectedPhoneNumber } from "./phoneNumberSelectors";
import { getSelectedPanel } from "./phoneNumbersSelectors";
import {
    getSubscription,
    getSubscriptionCode,
    getSubscriptionFinancingCode,
    getSubscriptionFinancingDuration,
    getSubscriptionFinancingType,
    getSubscriptions,
    hasSubscriptions as hasSubscriptionsSelector,
    isEsimSelected as isEsimSelectedSelector,
    isSimFromStoreSelected as isSimFromStoreSelectedSelector,
    isLease as isLeaseSelector,
    isReSwitch as isReSwitchSelector,
    isSubscriptionBroadband as isSubscriptionBroadbandSelector,
    isServiceSubscription as isServiceSubscriptionsSelector,
    isSwitchOrReSwitch as isSwitchOrReSwitchSelector,
    isTeliaTryggSubscription,
    isSubscriptionKeepExisting,
} from "./subscriptionsSelectors";
import {
    CART_ITEM_SKU_TYPES,
    CART_ITEM_SUBSCRIPTION_TYPES,
    CART_ITEM_TYPE,
    COMMITMENT_UPFRONT_TYPE,
    CUSTOMER_TYPE,
    DELIVERY_TYPE,
    ChangeType,
    CustomerType,
    DISCOUNT_TYPE,
    FINANCING_TYPE,
    FORM_ID,
    MAX_AGE_BARN,
    MAX_AGE_UNG,
    MAX_NUMBER_OF_ALLOWED_NEW_SUBSCRIPTIONS,
    MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS,
    MAX_PRICE_FOR_NEW_NUMBER,
} from "../../../../../constants";
import {
    DisclaimerItem,
    ICartItem,
    ICartItemOther,
    OfferingCode,
    RootState,
    SubscriptionType,
} from "../../../../../models";
import { CheckoutSubscription } from "../../../../../models/state";
import { getFormattedPrice } from "../../../../../shared/priceFormatter";
import { isPersonalDetails, State as AppState, getHwOnlyMaximumUpfronLimit, isB2B } from "../../../app/state/selectors";
import { allValidationRules, validateField, VALIDATION_FIELD } from "../../../rapidform/formValidation";
import { offeringCodesToDoubleDataAmount, offeringCodesToDoubleSpeed } from "../../../subscription-listing/consts";
import { isMhsSubscription } from "../../../../../shared/subscriptionCodeUtils";

export type State = CheckoutState;

// ==================
// ====== Cart ======
// ==================
export const isSimOnly = (state: State) => hasSubscriptionsSelector(state) && !hasCartHardware(state);

export const isHardwareOnly = (state: State) => !hasSubscriptionsSelector(state) && hasCartHardware(state);

export const isHwOnlyButRequireBankid = (state: RootState) =>
    !isB2B(state) && isHardwareOnly(state) && getTotalPriceUpfront(state) > getHwOnlyMaximumUpfronLimit(state);

export const isNewPhysicalSimRequired = (state: State) => {
    const allSubscriptions = _values(getSubscriptions(state));
    const subscriptionWithNotEsim = _find(allSubscriptions, (subscription) => {
        const customerType = getPhoneNumberCheckType(state, subscription.id);
        const isEsimSelected = isEsimSelectedSelector(state, subscription.id);
        const isSimFromStore = isSimFromStoreSelectedSelector(state, subscription.id);
        const isServiceSubscription = isServiceSubscriptionsSelector(state, subscription.id);

        return (
            (
                [CUSTOMER_TYPE.NEW, CUSTOMER_TYPE.PORT_IN, CUSTOMER_TYPE.NEW_OR_PORT_IN] as (CustomerType | undefined)[]
            ).includes(customerType) &&
            !isEsimSelected &&
            !isSimFromStore &&
            !isServiceSubscription
        );
    });

    return !!subscriptionWithNotEsim;
};

export const hasPhysicalSimService = (state: State) =>
    getFlattenedCartContent(state).some(
        (item) =>
            item.type === CART_ITEM_TYPE.SERVICE &&
            (item as ICartItemOther).isEsim === false &&
            (item as ICartItemOther).isExistingSim !== true,
    );

export const isDigitalDelivery = (state: State) =>
    !isNewPhysicalSimRequired(state) && isSimOnly(state) && !hasPhysicalSimService(state);

export const getSubscriptionChangeType = (state: State, subscriptionId?: string): ChangeType | undefined => {
    if (getPhoneNumberCheckType(state, subscriptionId) === CUSTOMER_TYPE.EXISTING) {
        return getSubscription(state, subscriptionId)?.changeType;
    }
    return undefined;
};

export const getSkuByBundleId = (state: State, bundleId: string) => {
    const cartItem = getItemByBundleId(state, bundleId)[0];
    if (cartItem && CART_ITEM_SKU_TYPES.includes(cartItem.type)) {
        return cartItem.id;
    }
    return null;
};

// TODO: Fix for Personal Details page
export const getSubscriptionPreviousServiceProvider = (state: State, subscriptionId?: string): string | undefined => {
    if (getPhoneNumberCheckType(state, subscriptionId) === CUSTOMER_TYPE.PORT_IN) {
        return getSubscription(state, subscriptionId)?.previousServiceProvider;
    }
    return undefined;
};

export const isOnlySubscriptionsToExistingCustomers = (state: State) => {
    const subscriptionInCart = getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES);
    const nonExisting = subscriptionInCart.find((subscription) => {
        return getPhoneNumberCheckType(state, subscription.bundleId) !== CUSTOMER_TYPE.EXISTING;
    });

    return _size(subscriptionInCart) > 0 && !nonExisting && !hasCartHardware(state);
};

export const hasSwitchOrReSwitchInCart = (state: State) => {
    const subscriptionInCart = getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES);
    return subscriptionInCart.some((subscription) => isSwitchOrReSwitchSelector(state, subscription.bundleId));
};

export const hasLeaseInCart = (state: State) => {
    const subscriptionInCart = getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES);
    return subscriptionInCart.some((subscription) => isLeaseSelector(state, subscription.bundleId));
};

export const hasReSwitchInCart = (state: State) => {
    const subscriptionInCart = getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES);
    return subscriptionInCart.some((subscription) => isReSwitchSelector(state, subscription.bundleId));
};

export const hasBroadbandInCart = (state: State) => {
    const subscriptionInCart = getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES);
    return subscriptionInCart.some((subscription) => isSubscriptionBroadbandSelector(state, subscription.bundleId));
};

export const hideNewPhoneNumberArea = (state: State) => {
    let handsetItemInCart = getAllItemsOfType(state, CART_ITEM_TYPE.HANDSET);
    if (_isEmpty(handsetItemInCart)) {
        handsetItemInCart = getAllItemsOfType(state, CART_ITEM_TYPE.HANDSET_PREOWNED);
    }
    const isNewCustomer = getSelectedPanel(state) === CUSTOMER_TYPE.NEW;
    return isNewCustomer && _get(handsetItemInCart[0], "price.originalSalesPrice", 0) > MAX_PRICE_FOR_NEW_NUMBER;
};

export const hideExistingPhoneNumberArea = (state: State) => {
    let handsetItemInCart = getAllItemsOfType(state, CART_ITEM_TYPE.HANDSET);
    if (_isEmpty(handsetItemInCart)) {
        handsetItemInCart = getAllItemsOfType(state, CART_ITEM_TYPE.HANDSET_PREOWNED);
    }
    const isExistingCustomer = CUSTOMER_INFO_SELECTORS.getCurrentType(state) === CUSTOMER_TYPE.EXISTING;
    const isExistingPrepaidKBDSubscription = CUSTOMER_INFO_SELECTORS.getCurrentIsExistingPrepaidKBDSubscription(state);
    return (
        isExistingCustomer &&
        isExistingPrepaidKBDSubscription &&
        _get(handsetItemInCart[0], "price.originalSalesPrice", 0) > MAX_PRICE_FOR_NEW_NUMBER
    );
};

// Same function is in Styleguide
const sortShoppingCart = (item: ICartItem) => {
    const cartItemTypes: Record<string, number> = {
        WEBDEAL: 10,
        HANDSET: 20,
        TABLET: 30,
        MODEM: 40,
        WEARABLE: 50,
        CONNECTED_DEVICE: 60,
        SUBSCRIPTION: 70,
        SUBSCRIPTION_DRAFT: 70,
        SPECIALPRODUCT: 71,
        SIM: 80,
        NEW_NUMBER: 90,
        SERVICE: 100,
        ACCESSORY: 110,
        VOUCHER: 130,
    };

    return cartItemTypes[item.type];
};

export const getTotalDisclaimerPrices = (state: State) => {
    const cartItems = getCartContent(state);
    const results = _compact(
        _flatten(
            _sortBy(cartItems, [sortShoppingCart]).map((cartItem) => {
                return getTotalDisclaimerPrice(state, cartItem);
            }),
        ),
    );

    const selectedDelivery = getEnhancedDeliveryInfoSelected(state);
    if (selectedDelivery) {
        results.push({
            name: "Levering",
            description: selectedDelivery.label,
            total: selectedDelivery.price,
        });
    }

    return results;
};

const getLeaseHardwareDescription = (
    leaseMonths: string | number | undefined,
    monthly: number,
    upfront: number | undefined,
) => {
    if (leaseMonths === 0) {
        return "";
    }

    const descriptions = [`Delbetaling i ${leaseMonths} md: ${monthly},-/md`];

    if (upfront) {
        descriptions.push(`Betale nå: ${upfront},-`);
    }

    return descriptions;
};

const getTotalDisclaimerPrice = (state: State, cartItem: ICartItem): DisclaimerItem | DisclaimerItem[] => {
    const subscriptionProduct = _find(cartItem.items, (item) => CART_ITEM_SUBSCRIPTION_TYPES.includes(item.type));
    const financing = getSubscriptionFinancingType(state, subscriptionProduct?.bundleId) || "commitment";
    const isKeepExisting = isSubscriptionKeepExisting(state, subscriptionProduct?.bundleId);
    const leaseMonths = cartItem.leaseMonths;
    const hasUpfrontDiscount = cartItem.discount?.value.upfront;
    const discountedPrice = (cartItem.price.upfront || 0) - (cartItem.discount?.value.upfront || 0);
    const total = (hasUpfrontDiscount ? discountedPrice : cartItem.price.total || cartItem.price.upfront) || 0;
    // SIMONLY
    if (CART_ITEM_SUBSCRIPTION_TYPES.includes(cartItem.type)) {
        const monthlyPrice = (cartItem.price.monthly || 0) - (cartItem.discount?.value?.monthly || 0);
        // Årsrabatt
        if (cartItem.discount?.types?.some((discountType) => discountType.id === "commitment")) {
            return {
                description: `Bindingstid i 12 md: ${monthlyPrice},-/md`,
                name: `${cartItem.name} mobilabonnement`,
                total: monthlyPrice * 12,
            } as const;
        }

        // No commitment
        return {
            name: `${cartItem.name}${cartItem.bundleId && isTeliaTryggSubscription(state, cartItem?.bundleId) ? "" : " mobilabonnement"}`,
            description: "Ingen bindingstid",
            total: monthlyPrice,
        } as const;
    }

    // Upfront
    if (!subscriptionProduct) {
        const quantity = cartItem.quantity.value;
        return {
            name: cartItem.name,
            description: quantity > 1 ? `${discountedPrice},-/stk` : "",
            total: total * quantity,
        } as const;
    }

    // LEASE
    if (financing === FINANCING_TYPE.LEASE) {
        const cartItemMonthlyPrice = (cartItem.price.monthly || 0) - (cartItem.discount?.value?.monthly || 0);
        const subscriptionItemMonthlyPrice =
            (subscriptionProduct.price.monthly || 0) - (subscriptionProduct.discount?.value?.monthly || 0);
        return [
            {
                name: cartItem.name,
                total,
                description: getLeaseHardwareDescription(leaseMonths, cartItemMonthlyPrice, cartItem.price.upfront),
            } as const,
            {
                name: `${subscriptionProduct.name} mobilabonnement`,
                total:
                    ((subscriptionProduct.price.monthly || 0) - (subscriptionProduct.discount?.value?.monthly || 0)) *
                    12,
                description: `Bindingstid i 12 md: ${subscriptionItemMonthlyPrice},-/md`,
            } as const,
        ];
    }

    // SWITCH
    const monthlyPrice = (cartItem.price.monthly || 0) - (cartItem.discount?.value?.monthly || 0);
    return [
        {
            name: cartItem.name,
            total: (cartItem.price.upfront || cartItem.price.total || 0) - (cartItem.discount?.value.upfront || 0),
            description: `24 md. rentefri delbetaling: ${monthlyPrice},-/md`,
        },
        {
            name: `${subscriptionProduct.name}${!isKeepExisting ? " mobilabonnement" : ""}`,
            total: (subscriptionProduct.price.monthly || 0) - (subscriptionProduct.discount?.value?.monthly || 0),
            description: "Ingen bindingstid",
        } as const,
    ];
};

export const getPaymentTooltip = (state: State, subscriptionId: string) => {
    if (!getSubscriptionCode(state, subscriptionId)) {
        return isLeaseSelector(state, subscriptionId);
    }
    return false;
};
const getMonthlyPriceDetailForHardware = (state: State, product: ICartItem, subscription: CheckoutSubscription) => {
    const financing = getSubscriptionFinancingType(state, subscription.id);

    // Leasing cases: Handset, Tablet, Wearable and Modem
    if (financing === FINANCING_TYPE.LEASE) {
        let duration = getSubscriptionFinancingDuration(state, subscription.id);
        const code = getSubscriptionFinancingCode(state, subscription.id);
        if (code === COMMITMENT_UPFRONT_TYPE) {
            duration = 0;
        }

        const leaseMonthly = _get(product, "price.monthly", "NaN");
        const leaseMonthlyDiscount = _get(product, "discount.value.monthly", "NaN");
        const leaseMonthlyPrice =
            leaseMonthly !== "NaN" && leaseMonthlyDiscount !== "NaN"
                ? `${getFormattedPrice(leaseMonthly - leaseMonthlyDiscount)}/md.`
                : "NaN";

        switch (product.type) {
            case CART_ITEM_TYPE.HANDSET:
            case CART_ITEM_TYPE.HANDSET_PREOWNED:
                return { label: `Delbetaling telefon ${duration} md.`, value: leaseMonthlyPrice };
            case CART_ITEM_TYPE.WEARABLE:
                return { label: `Delbetaling smartklokke ${duration} md.`, value: leaseMonthlyPrice };
            case CART_ITEM_TYPE.CONNECTED_DEVICE:
                return { label: `Delbetaling av Smarthjem produkt ${duration} md.`, value: leaseMonthlyPrice };
            case CART_ITEM_TYPE.TABLET:
                return { label: `Delbetaling nettbrett ${duration} md.`, value: leaseMonthlyPrice };
            case CART_ITEM_TYPE.MODEM:
                return { label: `Delbetaling ruter ${duration} md.`, value: leaseMonthlyPrice };
        }

        return { label: `Delbetaling produkt ${duration} md.`, value: leaseMonthlyPrice };
    }

    // Svitsj case
    const switchMontlyPrice = `${getFormattedPrice(_get(product, "price.monthly", 0))}/md.`;
    return { label: `Delbetaling telefon 24 md.`, value: switchMontlyPrice };
};

export const getMonthlyPriceDetails = (state: State) => {
    // Skip details if simonly
    if (isSimOnly(state)) {
        return [];
    }

    const cartItemsWithMonthlyPrice = getFlattenedCartContent(state).filter((item) => _get(item, "price.monthly"));

    return _compact(
        cartItemsWithMonthlyPrice.map((item) => {
            const subscription = getSubscription(state, item.bundleId);

            switch (item.type) {
                case CART_ITEM_TYPE.SUBSCRIPTION:
                case CART_ITEM_TYPE.SUBSCRIPTION_DRAFT:
                case CART_ITEM_TYPE.SERVICE:
                    return {
                        label: item.name,
                        value: `${getFormattedPrice(
                            (item.price.monthly || 0) - _get(item, "discount.value.monthly", 0),
                        )}/md.`,
                    };
                case CART_ITEM_TYPE.HANDSET:
                case CART_ITEM_TYPE.HANDSET_PREOWNED:
                case CART_ITEM_TYPE.WEARABLE:
                case CART_ITEM_TYPE.CONNECTED_DEVICE:
                case CART_ITEM_TYPE.TABLET:
                case CART_ITEM_TYPE.MODEM:
                    return subscription ? getMonthlyPriceDetailForHardware(state, item, subscription) : null;
            }
            return null;
        }),
    );
};

export const getDraftItemsWithOfferingCode = (state: State, offeringCode: OfferingCode): ICartItem[] => {
    const subscriptionDraftItemsInCart = getAllItemsOfType(state, CART_ITEM_TYPE.SUBSCRIPTION_DRAFT);
    const cartItems: ICartItem[] = [];

    subscriptionDraftItemsInCart.forEach((draftItem) => {
        if (draftItem.bundleId) {
            const code = getSubscriptionCode(state, draftItem.bundleId);
            if (code === offeringCode) {
                cartItems.push(draftItem);
            }
        }
    });

    return cartItems;
};

export const hasAtLeaseOneSubscriptionWithConvergenceOffer = (state: State): boolean => {
    const isConvergenceCustomer = getCustomerTypeGet(state) === CUSTOMER_TYPE.EXISTING;
    const allSubscriptions = _values(getSubscriptions(state));
    const hasOneSubscriptionWithConvergenceOffer = !!_find(allSubscriptions, (subscription) => {
        const offeringCode = getSubscriptionCode(state, subscription.id);
        const pricePlan = _split(offeringCode, ".")[0] as SubscriptionType;
        return !!offeringCodesToDoubleDataAmount[pricePlan] || offeringCodesToDoubleSpeed.includes(pricePlan);
    });

    return isConvergenceCustomer && hasOneSubscriptionWithConvergenceOffer;
};

// =====================
// ====== Payment ======
// =====================
export const getTotalPaymentPrice = (state: State) => getTotalPriceUpfront(state) + getDeliveryInfoSelectedPrice(state);

// ====================
// ====== Member ======
// ====================
const DATE_FORMAT_DDMMYYYY_WITH_SEPARATION = (separator: string) => ["DD", "MM", "YYYY"].join(separator);
const isValidDate = (date: string): boolean => {
    return validateField(date, allValidationRules[VALIDATION_FIELD.BIRTHDATE]) === null;
};
const getProductUserBirthdate = (state: State) => {
    const { values } = getFormById(state, FORM_ID.ADD_MEMBER);
    const isProductUserDifferentThanLegalOwner = CUSTOMER_INFO_SELECTORS.isProductUserDifferentThanLegalOwner(state);
    const birthDate = isProductUserDifferentThanLegalOwner
        ? _get(values, `member.${VALIDATION_FIELD.BIRTHDATE}`, undefined)
        : CUSTOMER_INFO_SELECTORS.getLegalOwnerBirthdate(state);
    return birthDate;
};
const getAge = (birthDate: string): number => {
    const now = moment();
    const birthDateMoment = moment(birthDate, DATE_FORMAT_DDMMYYYY_WITH_SEPARATION("."));
    const age = now.diff(birthDateMoment, "years", false);
    return age;
};
export const isProductUserChild = (state: State): boolean => {
    const birthDate = getProductUserBirthdate(state);
    return isValidDate(birthDate) && getAge(birthDate) <= MAX_AGE_BARN;
};
export const isProductUserYoung = (state: State): boolean => {
    const birthDate = getProductUserBirthdate(state);
    return isValidDate(birthDate) && getAge(birthDate) <= MAX_AGE_UNG;
};
export const getCampaignId = (): string => {
    return "";
};
export const getIsCurrentAllowedWithCampaignId = (state: State, campaignId: string): boolean => {
    const nonAllowedCampaignIds = CUSTOMER_INFO_SELECTORS.getCurrentCampaignIdsNotAllowed(state);
    return !_includes(nonAllowedCampaignIds, campaignId);
};

// ==================
// ====== MISC ======
// ==================
export const numberOfSubscriptions = (state: State): number => {
    // Subscriptions added to cart in this order
    const subscriptions = _values(getSubscriptions(state));
    const numberOfNewSubscriptions = _size(subscriptions);

    // Subscriptions existing already
    const selectedBan = getAccountId(state);
    const accountIdsWithTypes = getAccountsWithSubscriptions(state);
    const currentAccount = accountIdsWithTypes.find((account) => account.accountId === selectedBan);
    const existingSubscriptions = _get(currentAccount, "subscriptions") || [];
    const numberOfExistingSubscriptions = _size(existingSubscriptions);

    // Subscriptions added to cart that are numbers already on BAN
    const intersection = existingSubscriptions.filter((existingSub) =>
        subscriptions.find((sub) => getSelectedPhoneNumber(state, sub.id) === existingSub.phoneNumber),
    );

    return numberOfNewSubscriptions + numberOfExistingSubscriptions - intersection.length;
};
export const numberOfNewSubscriptions = (state: State): number => {
    const newSubscriptions = _values(getSubscriptions(state)).filter(
        (sub) => _get(sub, "phoneNumber.check.type", null) === CUSTOMER_TYPE.NEW,
    );
    return _size(newSubscriptions);
};

export const numberOfCountableNewSubscriptions = (state: State): number => {
    const newSubscriptions = _values(getSubscriptions(state)).filter(
        (sub) => _get(sub, "phoneNumber.check.type", null) === CUSTOMER_TYPE.NEW && isMhsSubscription(sub.code),
    );
    return _size(newSubscriptions);
};

export const numberOfPortInSubscriptions = (state: State): number => {
    const newSubscriptions = _values(getSubscriptions(state)).filter(
        (sub) => _get(sub, "phoneNumber.check.type", null) === CUSTOMER_TYPE.PORT_IN,
    );
    return _size(newSubscriptions);
};
export const numberOfPortInEsimSubscriptions = (state: State): number => {
    const newSubscriptions = _values(getSubscriptions(state)).filter(
        (sub) => _get(sub, "phoneNumber.check.type", null) === CUSTOMER_TYPE.PORT_IN && _get(sub, "isEsimSelected"),
    );
    return _size(newSubscriptions);
};
export const numberOfExistingSubscriptions = (state: State): number => {
    const newSubscriptions = _values(getSubscriptions(state)).filter(
        (sub) => _get(sub, "phoneNumber.check.type", null) === CUSTOMER_TYPE.EXISTING,
    );
    return _size(newSubscriptions);
};
export const numberOfBanInUseByExistingPhoneNumbersInCart = (state: State): number => {
    const existingPhoneNumbersInCart = _values(getSubscriptions(state))
        .filter((sub) => _get(sub, "phoneNumber.check.type", null) === CUSTOMER_TYPE.EXISTING)
        .map((sub) => _get(sub, "phoneNumber.selectedPhoneNumber", null));

    const accountsWithSubscriptions = getAccountsWithSubscriptions(state);
    const bansInUse: Record<string, boolean> = {};

    _forEach(accountsWithSubscriptions, (account) => {
        _forEach(account.subscriptions, (subscription) => {
            if (_includes(existingPhoneNumbersInCart, subscription.phoneNumber)) {
                bansInUse[account.accountId] = true;
            }
        });
    });

    return _size(_keys(bansInUse));
};

export const getPhoneNumbersInCart = (state: State): string[] => {
    const subscriptions = getSubscriptions(state);
    const phoneNumbers: string[] = [];
    _values(subscriptions).forEach((subscription) => {
        const phoneNumber = getSelectedPhoneNumber(state, subscription.id);
        if (phoneNumber) {
            phoneNumbers.push(phoneNumber);
        }
    });
    return phoneNumbers;
};
export const getExistingSubscriptionsNotInCart = (state: State) => {
    const existingSubscriptions = getExistingSubscriptionsInGroup(state);

    const transformedExistingSubscriptionNotInCart = existingSubscriptions.map((existingMember) => {
        return {
            ...existingMember,
            subscriptionName: _get(existingMember, "name", ""),
            name: `${existingMember.firstName} ${existingMember.lastName}`,
            number: existingMember.phoneNumber,
            isExisting: true,
        };
    });

    const numbersInCart = getPhoneNumbersInCart(state);
    return transformedExistingSubscriptionNotInCart.filter((sub) => !numbersInCart.includes(sub.number));
};
export const hasReachedMaximumNumberOfSubscriptions = (state: State): boolean =>
    numberOfSubscriptions(state) > MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS && !isHardwareOnly(state);
export const hasReachedMaximumNumberOfNewSubscriptions = (state: State): boolean =>
    numberOfCountableNewSubscriptions(state) >= MAX_NUMBER_OF_ALLOWED_NEW_SUBSCRIPTIONS;
export const isMultipleSubscriptions = (state: State): boolean => {
    const subscriptionCount = _size(getSubscriptions(state));

    if (hasExternalLegalOwner(state)) {
        return subscriptionCount > 0;
    }
    return subscriptionCount > 1;
};

export const shouldOnlyShowSlideoutCart = (state: State & AppState) => isPersonalDetails(state);

export const isSmartDiscountPriceLowerThanNormalPrice = (state: State) => {
    const firstBundleItem = getFirstBundleItemInCart(state);
    const discountType = _get(firstBundleItem, "discount.type");

    switch (discountType) {
        case DISCOUNT_TYPE.MAIN_FINAL: {
            const smartDiscountPrice =
                _get(firstBundleItem, "price.upfront", 0) - _get(firstBundleItem, "discount.value.upfront", 0);
            const price = _get(firstBundleItem, "price.total");
            if (price !== undefined && smartDiscountPrice !== undefined && smartDiscountPrice >= price) {
                return false;
            }
        }
    }

    return true;
};

export const getDeliveryInformation = (state: State): { title: string; description: string } => {
    const deliveryAddress = getDeliveryInfoAddress(state);
    const deliveryDescription = getDeliveryDescription(state);
    const isDigital = isDigitalDelivery(state);
    const isPhysicalSimRequired = isNewPhysicalSimRequired(state);
    const isSimOnlyInCart = isSimOnly(state);
    const isOnlyExistingCustomers = isOnlySubscriptionsToExistingCustomers(state);
    const legalOwnerAddress = CUSTOMER_INFO_SELECTORS.getLegalOwnerAddress(state);
    const legalOwnerCity = CUSTOMER_INFO_SELECTORS.getLegalOwnerFullCity(state);

    let deliveryDescriptionTitle = "";
    let description = deliveryDescription;

    if (isPhysicalSimRequired && isSimOnlyInCart && getDeliveryInfoType(state) === DELIVERY_TYPE.PICK_UP_IN_SHOP) {
        return { title: TEXT.DELIVERY.CLICK_AND_FETCH.HEADING, description: deliveryDescription };
    }

    if (isOnlyExistingCustomers) {
        deliveryDescriptionTitle = `${TEXT.RECEIPT.ORDER_TYPE}:`;
        description = `${TEXT.RECEIPT.UPGRADE}`;
    }

    if (!isDigital && !isPhysicalSimRequired && !isSimOnlyInCart) {
        return { title: deliveryDescriptionTitle, description };
    }

    if (isPhysicalSimRequired && isSimOnlyInCart) {
        return {
            title: "Brev i postkassen",
            description: _isEmpty(deliveryAddress)
                ? legalOwnerAddress + ", " + legalOwnerCity
                : deliveryAddress.street + ", " + deliveryAddress.postcode + " " + deliveryAddress.city,
        };
    }

    return { title: "", description: "" };
};
